import React from 'react';
import moment from 'moment';
import bemBlock from '@gik/core/utils/bemBlock';
import { useCalendarStore } from '@gik/calendar/store/CalendarStore';
import { LoadingSpinner } from '@gik/ui';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import CalendarBar from './CalendarBar';
import CalendarDaysList from './CalendarDaysList';

export interface ICalendarProps {
  /**
   * Initial month as a YYYY-MM formatted string
   */
  month?: string;
}

export function Calendar({
  month = moment().format(DateTimeUtils.MonthFormat), // default to the current month
}: ICalendarProps): React.ReactElement {
  const bem = bemBlock('inkind-calendar');

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const setSelectedMonth = useCalendarStore(state => state.setSelectedMonth);
  const setReloadFunction = useCalendarStore(state => state.setReloadFunction);
  const selectedMonth = useCalendarStore(state => state.selectedMonth);

  const { data: inkind, error: inkindError } = useInkind(inkindRouteId);

  function gotoPrevMonth() {
    setSelectedMonth(moment(selectedMonth).add(-1, 'month').format(DateTimeUtils.MonthFormat));
  }

  function gotoNextMonth() {
    setSelectedMonth(moment(selectedMonth).add(1, 'month').format(DateTimeUtils.MonthFormat));
  }

  function handleListInit(reload: Function) {
    setReloadFunction(reload);
  }

  // update selected month when the month property changes
  React.useEffect(() => {
    setSelectedMonth(month);
  }, [month, setSelectedMonth]);

  if (inkindError) return <div>{inkindError.message}</div>;

  const isLoading = !inkind;
  if (isLoading) return <LoadingSpinner center />;

  return (
    <div className={bem()}>
      <header className="no-print">
        <CalendarBar
          selectedMonth={selectedMonth}
          className={bem('header')}
          onPrev={gotoPrevMonth}
          onNext={gotoNextMonth}
        />
      </header>
      <main>
        {inkind && <CalendarDaysList onPrev={gotoPrevMonth} onNext={gotoNextMonth} onInit={handleListInit} />}
      </main>
    </div>
  );
}
